import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import type { AppUser } from "../../services/userService";
import type { NoteRefArt } from "../../services/vorgangService";
import type { BezugKandidat } from "./bezugKandidaten";
import type { KommentarSenden } from "./KommentarEingabe";
import { KommentarFaden, type KommentarAendern } from "./KommentarFaden";
import type { Kommentar } from "./kommentarModell";
import { NebenStelle } from "./NebenStelle";
import "./notizbloecke.css";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/**
 * Der Faden AN EINER MARKIERTEN TEXTSTELLE (PO 18.09.2026: „dann zeigt sich
 * das Kommentar auch beim Hovern und es ist markiert").
 *
 * ══ NEBEN DER STELLE, AUF IHRER HÖHE (PO 28.09.2026) ═══════════════════════
 * „Kommentarfenster bitte auf selber Höhe wie der Kommentar" — der Faden
 * steht wie in Word neben dem Text, nicht darunter (`platzNebenStelle`).
 *
 * ══ AM RECHTECK DER STELLE, NICHT IM TEXT ══════════════════════════════════
 * Als Portal am `body` mit `position: fixed`: Im Notizfeld wäre der Faden
 * Text, den man versehentlich löscht — und die Notizfläche steckt in einer
 * Karte, die über ihren Rand nicht hinauszeichnen darf.
 *
 * ══ ÜBERFAHREN ZEIGT, KLICKEN HÄLT ═════════════════════════════════════════
 * Beim Überfahren geht er auf und beim Weggehen wieder zu — zum LESEN reicht
 * das. Wer etwas TUN will (schreiben, abhaken, ändern), klickt; dann bleibt er
 * stehen, bis Escape oder ein Klick daneben kommt. Ohne das Festhalten wäre
 * der Weg vom Text zum Schreibfeld ein Zielen mit der Maus, bei dem jede
 * Abweichung den Faden schließt.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const BREITE = 320;

export const AnmerkFaden: React.FC<{
    /** Die markierte Stelle, an der er hängt. */
    stelle: HTMLElement;
    /** Das Notizfeld — der Faden stellt sich rechts daneben. */
    feld?: HTMLElement | null;
    kommentare: Kommentar[];
    users: AppUser[];
    /** Festgehalten (Klick) statt nur überfahren? */
    fest: boolean;
    onSchliessen: () => void;
    onNeu?: KommentarSenden;
    aktuellerUserId?: string;
    onAendern?: KommentarAendern;
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /** Der Haken (PO 28.09.2026) — bedienbar im festgehaltenen Faden. */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    kandidaten?: BezugKandidat[];
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    /** Die Maus ist in den (nur überfahrenen) Faden gewandert — festhalten. */
    onFesthalten?: () => void;
    mitAnhang?: boolean;
}> = ({
    stelle,
    feld,
    kommentare,
    users,
    fest,
    onSchliessen,
    onNeu,
    aktuellerUserId,
    onAendern,
    onLoeschen,
    onErledigt,
    kandidaten,
    onBezugOeffnen,
    onFesthalten,
    mitAnhang,
}) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const rechteck = useCallback(() => stelle.getBoundingClientRect(), [stelle]);

    useEffect(() => {
        if (!fest) return;
        const taste = (e: KeyboardEvent) => {
            if (e.key === "Escape") onSchliessen();
        };
        const daneben = (e: MouseEvent) => {
            const ziel = e.target as Node | null;
            if (ref.current?.contains(ziel)) return;
            if (stelle.contains(ziel)) return;
            // Die Auswahlliste hinter `@`/`#` hängt als Portal am `body` —
            // ein Klick hinein gehört zum Faden.
            if ((ziel as Element | null)?.closest?.("[data-popover-teil]")) return;
            onSchliessen();
        };
        alleDokumente.addEventListener("keydown", taste);
        // `mousedown` statt `click`: Ein Klick ins Feld darunter soll den Faden
        // schließen, bevor er dort den Cursor setzt.
        alleDokumente.addEventListener("mousedown", daneben);
        return () => {
            alleDokumente.removeEventListener("keydown", taste);
            alleDokumente.removeEventListener("mousedown", daneben);
        };
    }, [fest, onSchliessen, stelle]);

    return (
        <NebenStelle
            stelle={rechteck}
            feld={feld ?? null}
            breite={BREITE}
            className="nb-anmerk-faden"
            innerRef={ref}
            role="dialog"
            ariaLabel="Kommentare zu dieser Stelle"
            // Der Klick darf dem Feld nicht den Fokus nehmen und dort kein
            // Speichern-beim-Verlassen auslösen.
            onMouseDown={(e) => e.stopPropagation()}
        >
            {/* Wer mit der Maus hineinfährt, will etwas tun — dann bleibt der
                Faden stehen, statt beim Verlassen der Stelle zu verschwinden. */}
            <div onMouseEnter={fest ? undefined : onFesthalten}>
                <KommentarFaden
                    kommentare={kommentare}
                    users={users}
                    titel="Kommentare zu dieser Stelle"
                    {...(fest && onNeu ? { onNeu } : {})}
                    {...(aktuellerUserId ? { aktuellerUserId } : {})}
                    {...(fest && onAendern ? { onAendern } : {})}
                    {...(fest && onLoeschen ? { onLoeschen } : {})}
                    {...(fest && onErledigt ? { onErledigt } : {})}
                    {...(kandidaten ? { kandidaten } : {})}
                    {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                    {...(mitAnhang ? { mitAnhang } : {})}
                />
            </div>
        </NebenStelle>
    );
};
